import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Menu, 
  X, 
  Home, 
  Info, 
  Users, 
  Mail, 
  LogIn 
} from 'lucide-react';
import { Button } from '../ui/button.jsx';
import Footer from './Footer.jsx';
import { cn } from '../../lib/utils';

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const menuItems = [
    { text: 'Home', path: '/', icon: Home },
    { text: 'About', path: '/about', icon: Info },
    // { text: 'Admission', path: '/admission', icon: Users }, // Commented out - redirects to Contact
    { text: 'Admission', path: '/contact', icon: Users }, // Redirects to Contact page
    { text: 'Contact', path: '/contact', icon: Mail },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavigation = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <motion.header
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
          scrolled 
            ? "bg-white/95 backdrop-blur-md shadow-sm border-b border-border" 
            : "bg-black/20 backdrop-blur-sm"
        )}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <motion.div
              className="flex items-center cursor-pointer"
              onClick={() => navigate('/')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <div className={cn(
                "flex items-center justify-center w-10 h-10 rounded-full mr-3 shadow-lg overflow-hidden",
                scrolled ? "bg-white" : "bg-white/20 backdrop-blur-sm"
              )}>
                <img 
                  src="/logo192.png" 
                  alt="Bridge of Life Academy Logo" 
                  className="w-full h-full object-contain p-1"
                />
              </div>
              <div>
                <h1 className={cn(
                  "text-xl font-bold transition-colors duration-300",
                  scrolled ? "text-gray-900" : "text-white"
                )}>
                  Bridge of Life Academy
                </h1>
                <p className={cn(
                  "text-sm hidden sm:block transition-colors duration-300",
                  scrolled ? "text-gray-600" : "text-white/80"
                )}>
                  Excellence in Education
                </p>
              </div>
            </motion.div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-2">
              {menuItems.map((item, index) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                
                return (
                  <motion.div
                    key={item.text}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Button
                      variant={isActive ? "secondary" : "ghost"}
                      onClick={() => handleNavigation(item.path)}
                      className={cn(
                        "transition-all duration-300",
                        scrolled 
                          ? (isActive ? "bg-indigo-50 text-indigo-800" : "text-gray-700 hover:text-indigo-800 hover:bg-indigo-50")
                          : (isActive ? "bg-white/20 text-white" : "text-white/90 hover:text-white hover:bg-white/10")
                      )}
                    >
                      <Icon className="w-4 h-4 mr-2" />
                      {item.text}
                    </Button>
                  </motion.div>
                );
              })}
              
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: menuItems.length * 0.1 }}
              >
                <Button
                  onClick={() => handleNavigation('/login')}
                  className={cn(
                    "transition-all duration-300",
                    scrolled 
                      ? "bg-indigo-800 hover:bg-indigo-900 text-white"
                      : "bg-white/20 hover:bg-white/30 text-white border border-white/30 backdrop-blur-sm"
                  )}
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Login
                </Button>
              </motion.div>
            </nav>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "md:hidden",
                scrolled ? "text-gray-900 hover:bg-gray-100" : "text-white hover:bg-white/10"
              )}
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/50 z-40 md:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              className="fixed top-0 right-0 h-full w-80 bg-white z-50 shadow-xl md:hidden"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 20 }}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center">
                    <div className="flex items-center justify-center w-10 h-10 bg-white rounded-full mr-3 overflow-hidden shadow-md">
                      <img 
                        src="/logo192.png" 
                        alt="Bridge of Life Academy Logo" 
                        className="w-full h-full object-contain p-1"
                      />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">Bridge of Life</h2>
                      <p className="text-sm text-gray-600">Academy</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setMobileOpen(false)}
                  >
                    <X className="w-6 h-6" />
                  </Button>
                </div>

                <nav className="space-y-2">
                  {menuItems.map((item, index) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    
                    return (
                      <motion.div
                        key={item.text}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <Button
                          variant={isActive ? "secondary" : "ghost"}
                          onClick={() => handleNavigation(item.path)}
                          className={cn(
                            "w-full justify-start",
                            isActive && "bg-indigo-50 text-indigo-800"
                          )}
                        >
                          <Icon className="w-4 h-4 mr-3" />
                          {item.text}
                        </Button>
                      </motion.div>
                    );
                  })}
                  
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: menuItems.length * 0.1 }}
                    className="pt-4"
                  >
                    <Button
                      onClick={() => handleNavigation('/login')}
                      className="w-full bg-indigo-800 hover:bg-indigo-900 text-white"
                    >
                      <LogIn className="w-4 h-4 mr-3" />
                      Login
                    </Button>
                  </motion.div>
                </nav>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="flex-1 pt-16">
        <Outlet />
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Layout;